import React, { useState, useEffect, useCallback, memo, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemSecondaryAction,
  Divider,
  Paper,
  Switch,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIndicatorIcon,
} from "@mui/icons-material";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import AddQuestionDialog from "./interview-plan-editor/AddQuestionDialog";
import AddDimensionDialog from "./interview-plan-editor/AddDimensionDialog";
import PostInterviewStepsEditor from "./interview-plan-editor/PostInterviewStepsEditor";
import ControversialContentEditor from "./interview-plan-editor/ControversialContentEditor";
import {
  PostInterviewStep,
  ControversialContentConfig,
} from "../../../redux/reducers/assessmentReducer";

interface Dimension {
  analyze_question: string;
  guidance?: string;
  id?: string;
  answer_type?: string;
  answer_guidance?: string;
}

interface Question {
  id: string;
  question: string;
  question_type: string;
  would_interviewer_know_answer?: boolean;
  is_question_multi_part_explanation?: boolean;
  would_answer_overlap_another_question?: boolean;
}

interface RubricElement {
  score: number;
  description: string;
  example?: string;
}

interface InterviewQuestion {
  question: Question;
  dimensions?: Dimension[];
  rubric?: RubricElement[];
}

// PostInterviewStep type is imported from the reducer

interface InterviewPlan {
  scenario: {
    description: string;
    goal: string;
    category: string;
    applicant: string;
    evaluator: string;
    no_include?: string;
  };
  eval_criteria: string[];
  questions: InterviewQuestion[];
  editor_fields: {
    greeting: string;
    num_questions: number;
    expected_time_minutes: number;
    time_limit_minutes: number;
    allowed_qa_topics?: string[];
    ask_for_questions_before_start?: boolean;
    post_interview_steps?: PostInterviewStep[];
    check_for_suspicious?: boolean;
    check_for_seeking_clarification?: boolean;
    max_num_user_queries_per_question?: number;
    max_num_poorly_answered_questions?: number;
    min_words_for_chat?: number;
    max_words_for_chat?: number;
    additional_guidance?: string[];
    post_insight_timeout_seconds?: number;
    post_statement_timeout_seconds?: number;
    without_guidance?: string;
    controversial_content_config?: ControversialContentConfig | null;
  };
  discussion_topic: string;
  criteria_context: string;
  assessment_processing_type: "memory" | "form";
  configuration?: {
    min_num_turns: number;
    max_num_turns: number;
    min_num_questions_answered: number;
    non_job: boolean;
    positive_feedback: boolean;
    reflective_responses: boolean;
  };
}

interface InterviewPlanEditorProps {
  interviewPlan: InterviewPlan;
  onChange: (updatedPlan: InterviewPlan) => void;
  isEditing: boolean;
}

// Memoized Sortable Dimension Component
const SortableDimension = memo<{
  dimension: Dimension;
  dimensionIndex: number;
  questionId: string;
  isEditing: boolean;
  onDimensionChange: (
    questionId: string,
    dimensionIndex: number,
    field: keyof Dimension,
    value: any
  ) => void;
  onRemoveDimension: (questionId: string, dimensionIndex: number) => void;
}>(
  ({
    dimension,
    dimensionIndex,
    questionId,
    isEditing,
    onDimensionChange,
    onRemoveDimension,
  }) => {
    const {
      attributes: dimAttributes,
      listeners: dimListeners,
      setNodeRef: setDimNodeRef,
      transform: dimTransform,
      transition: dimTransition,
      isDragging: isDimDragging,
    } = useSortable({
      id: `${questionId}-dim-${dimensionIndex}`,
      disabled: !isEditing,
    });

    const dimStyle = useMemo(
      () => ({
        transform: CSS.Transform.toString(dimTransform),
        transition: dimTransition,
        opacity: isDimDragging ? 0.5 : 1,
      }),
      [dimTransform, dimTransition, isDimDragging]
    );

    return (
      <Card
        ref={setDimNodeRef}
        style={dimStyle}
        variant="outlined"
        sx={{ mb: 2, p: 2 }}
      >
        <Box display="flex" alignItems="flex-start">
          {isEditing && (
            <Box
              {...dimAttributes}
              {...dimListeners}
              sx={{
                cursor: "grab",
                mr: 1,
                mt: 1,
                "&:active": { cursor: "grabbing" },
              }}
            >
              <DragIndicatorIcon color="action" />
            </Box>
          )}
          <Box flex={1}>
            <TextField
              fullWidth
              label="Question"
              multiline
              rows={2}
              value={dimension.analyze_question}
              onChange={(e) =>
                onDimensionChange(
                  questionId,
                  dimensionIndex,
                  "analyze_question",
                  e.target.value
                )
              }
              disabled={!isEditing}
              sx={{ mb: 2 }}
              helperText="The main question to analyze in the candidate's response (editable)"
            />

            <TextField
              fullWidth
              label="Guidance"
              multiline
              rows={2}
              value={dimension.guidance || ""}
              onChange={(e) =>
                onDimensionChange(
                  questionId,
                  dimensionIndex,
                  "guidance",
                  e.target.value
                )
              }
              disabled={!isEditing}
              sx={{ mb: 2 }}
              helperText="Additional guidance for the interviewer (editable)"
            />

            <FormControl fullWidth disabled={!isEditing} sx={{ mb: 2 }}>
              <InputLabel>Answer Type</InputLabel>
              <Select
                value={dimension.answer_type || "string"}
                onChange={(e) =>
                  onDimensionChange(
                    questionId,
                    dimensionIndex,
                    "answer_type",
                    e.target.value
                  )
                }
                label="Answer Type"
              >
                <MenuItem value="unspecified">Unspecified</MenuItem>
                <MenuItem value="string">String</MenuItem>
                <MenuItem value="int">Integer</MenuItem>
                <MenuItem value="float">Float</MenuItem>
                <MenuItem value="boolean">Boolean</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Answer Guidance"
              multiline
              rows={2}
              value={dimension.answer_guidance || ""}
              onChange={(e) =>
                onDimensionChange(
                  questionId,
                  dimensionIndex,
                  "answer_guidance",
                  e.target.value
                )
              }
              disabled={!isEditing}
              sx={{ mb: 2 }}
              helperText="Optional guidance on how to answer this dimension"
            />

            {dimension.id && (
              <TextField
                fullWidth
                label="ID"
                value={dimension.id}
                disabled={true}
                sx={{ mb: 2 }}
                helperText="Unique identifier for this criteria (read-only)"
              />
            )}

            {isEditing && (
              <Box display="flex" justifyContent="flex-end">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveDimension(questionId, dimensionIndex);
                  }}
                  color="error"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>
        </Box>
      </Card>
    );
  }
);

SortableDimension.displayName = "SortableDimension";

// Sortable Question Component
interface SortableQuestionProps {
  question: InterviewQuestion;
  index: number;
  isEditing: boolean;
  onQuestionChange: (questionId: string, field: string, value: any) => void;
  onDimensionChange: (
    questionId: string,
    dimensionIndex: number,
    field: keyof Dimension,
    value: any
  ) => void;
  onAddDimension: (questionId: string, dimension: Dimension) => void;
  onRemoveDimension: (questionId: string, dimensionIndex: number) => void;
  onRemoveQuestion: (questionId: string) => void;
  onRubricChange: (
    questionId: string,
    score: number,
    field: "description" | "example",
    value: string
  ) => void;
  onDimensionReorder: (
    questionId: string,
    oldIndex: number,
    newIndex: number
  ) => void;
}

const SortableQuestion = memo<SortableQuestionProps>(
  ({
    question,
    index,
    isEditing,
    onQuestionChange,
    onDimensionChange,
    onAddDimension,
    onRemoveDimension,
    onRemoveQuestion,
    onRubricChange,
    onDimensionReorder,
  }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: question.question.id });

    const style = useMemo(
      () => ({
        transform: CSS.Transform.toString(transform),
        transition: transition,
        opacity: isDragging ? 0.5 : 1,
      }),
      [transform, transition, isDragging]
    );

    const [addDimensionDialog, setAddDimensionDialog] = useState(false);
    const [removeRubricDialog, setRemoveRubricDialog] = useState(false);
    const [newDimension, setNewDimension] = useState<Dimension>({
      analyze_question: "",
      guidance: "",
      id: "",
    });

    // Drag sensors for dimensions
    const dimensionSensors = useSensors(
      useSensor(PointerSensor, {
        activationConstraint: {
          distance: 8,
        },
      }),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
      })
    );

    const handleDimensionDragEnd = useCallback(
      (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
          const activeId = active.id as string;
          const overId = over.id as string;

          // Extract dimension indices from the IDs
          const activeIndex = parseInt(activeId.split("-dim-")[1]);
          const overIndex = parseInt(overId.split("-dim-")[1]);

          if (!isNaN(activeIndex) && !isNaN(overIndex)) {
            onDimensionReorder(question.question.id, activeIndex, overIndex);
          }
        }
      },
      [question.question.id, onDimensionReorder]
    );

    const handleAddDimension = useCallback(() => {
      onAddDimension(question.question.id, newDimension);
      setAddDimensionDialog(false);
      setNewDimension({
        analyze_question: "",
        guidance: "",
        id: "",
      });
    }, [question.question.id, onAddDimension, newDimension]);

    return (
      <Box ref={setNodeRef} style={style} sx={{ mb: 2 }}>
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              bgcolor: "grey.50",
              "&:hover": { bgcolor: "grey.100" },
              "& .MuiAccordionSummary-content": {
                margin: 0,
                py: 1,
              },
              position: "relative",
            }}
          >
            {/* Drag handle positioned absolutely within AccordionSummary */}
            {isEditing && (
              <Box
                {...attributes}
                {...listeners}
                sx={{
                  position: "absolute",
                  left: -40,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 1,
                  cursor: "grab",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 24,
                  height: 24,
                  padding: "4px",
                  "&:active": { cursor: "grabbing" },
                  "&:hover": {
                    bgcolor: "action.hover",
                    borderRadius: 1,
                  },
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <DragIndicatorIcon color="action" fontSize="small" />
              </Box>
            )}
            <Box display="flex" alignItems="center" width="100%">
              <Typography
                variant="body2"
                fontWeight="medium"
                sx={{
                  width: 90,
                  flexShrink: 0,
                  mr: 2,
                }}
              >
                Question {index + 1}
              </Typography>
              <Chip
                label={question.question.question_type}
                size="small"
                color="primary"
                sx={{
                  width: 110,
                  flexShrink: 0,
                  mr: 2,
                  "& .MuiChip-label": {
                    width: "100%",
                    textAlign: "center",
                    display: "block",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  flex: 1,
                  maxWidth: "60%", // Keep at 60% as requested
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  minWidth: 0,
                }}
              >
                {question.question.question.length > 80
                  ? `${question.question.question.substring(0, 80)}...`
                  : question.question.question}
              </Typography>
              <Box sx={{ ml: "auto", display: "flex", alignItems: "center" }}>
                {isEditing && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveQuestion(question.question.id);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {/* Question Text */}
            <Box mb={3}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Typography variant="subtitle2">Question Text</Typography>
              </Box>
              <TextField
                fullWidth
                multiline
                rows={3}
                value={question.question.question}
                onChange={(e) =>
                  onQuestionChange(
                    question.question.id,
                    "question",
                    e.target.value
                  )
                }
                disabled={!isEditing}
              />
            </Box>

            {/* Additional Question Properties */}
            <Box mb={3}>
              <Typography variant="subtitle2" gutterBottom>
                Question Properties
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <FormControl fullWidth disabled={!isEditing}>
                  <InputLabel>Question Type</InputLabel>
                  <Select
                    value={question.question.question_type}
                    onChange={(e) =>
                      onQuestionChange(
                        question.question.id,
                        "question_type",
                        e.target.value
                      )
                    }
                    label="Question Type"
                  >
                    <MenuItem value="general">General</MenuItem>
                    <MenuItem value="informational">Informational</MenuItem>
                    <MenuItem value="behavioral">Behavioral</MenuItem>
                    <MenuItem value="scenario">Scenario</MenuItem>
                    <MenuItem value="background">Background</MenuItem>
                  </Select>
                </FormControl>
                <FormControlLabel
                  control={
                    <Switch
                      checked={
                        question.question.would_interviewer_know_answer || false
                      }
                      onChange={(e) =>
                        onQuestionChange(
                          question.question.id,
                          "would_interviewer_know_answer",
                          e.target.checked
                        )
                      }
                      disabled={!isEditing}
                    />
                  }
                  label="Would interviewer know the answer?"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={
                        question.question.is_question_multi_part_explanation ||
                        false
                      }
                      onChange={(e) =>
                        onQuestionChange(
                          question.question.id,
                          "is_question_multi_part_explanation",
                          e.target.checked
                        )
                      }
                      disabled={!isEditing}
                    />
                  }
                  label="Is this a multi-part explanation question?"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={
                        question.question
                          .would_answer_overlap_another_question || false
                      }
                      onChange={(e) =>
                        onQuestionChange(
                          question.question.id,
                          "would_answer_overlap_another_question",
                          e.target.checked
                        )
                      }
                      disabled={!isEditing}
                    />
                  }
                  label="Would answer overlap with another question?"
                />
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Dimensions with Drag and Drop */}
            <Box mb={3}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Typography variant="subtitle2">
                  Dimensions ({question.dimensions?.length || 0})
                </Typography>
                {isEditing && (
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setAddDimensionDialog(true)}
                  >
                    Add Dimension
                  </Button>
                )}
              </Box>

              {question.dimensions && question.dimensions.length > 0 ? (
                <DndContext
                  sensors={dimensionSensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDimensionDragEnd}
                >
                  <SortableContext
                    items={question.dimensions.map(
                      (_, idx) => `${question.question.id}-dim-${idx}`
                    )}
                    strategy={verticalListSortingStrategy}
                  >
                    {question.dimensions.map((dimension, idx) => (
                      <SortableDimension
                        key={`${question.question.id}-dim-${idx}`}
                        dimension={dimension}
                        dimensionIndex={idx}
                        questionId={question.question.id}
                        isEditing={isEditing}
                        onDimensionChange={onDimensionChange}
                        onRemoveDimension={onRemoveDimension}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No dimensions defined
                </Typography>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Rubric Section - Show "Add Rubric" button or existing rubric */}
            {(() => {
              const hasRubric = question.rubric && question.rubric.length > 0;

              if (!hasRubric) {
                // Show "Add Rubric" button when no rubric exists (only in edit mode)
                return isEditing ? (
                  <Box>
                    <Divider sx={{ my: 2 }} />
                    <Box display="flex" justifyContent="center" py={2}>
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          // Add default 5-level rubric
                          onQuestionChange(question.question.id, "rubric", [
                            {
                              score: 5,
                              description:
                                "Exceptional - Exceeds all expectations",
                              example: "",
                            },
                            {
                              score: 4,
                              description:
                                "Strong - Meets most expectations well",
                              example: "",
                            },
                            {
                              score: 3,
                              description:
                                "Adequate - Meets basic expectations",
                              example: "",
                            },
                            {
                              score: 2,
                              description: "Developing - Below expectations",
                              example: "",
                            },
                            {
                              score: 1,
                              description:
                                "Inadequate - Does not meet expectations",
                              example: "",
                            },
                          ]);
                        }}
                      >
                        Add Rubric
                      </Button>
                    </Box>
                  </Box>
                ) : null;
              }

              // Show existing rubric with "Remove Rubric" button
              return (
                <Box>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1}
                  >
                    <Typography variant="subtitle2">Rubric</Typography>
                    {isEditing && (
                      <Button
                        size="small"
                        color="error"
                        onClick={() => setRemoveRubricDialog(true)}
                      >
                        Remove Rubric
                      </Button>
                    )}
                  </Box>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50" }}>
                    {question
                      .rubric!.sort((a, b) => b.score - a.score)
                      .map((rubricItem) => (
                        <Box key={rubricItem.score} sx={{ mb: 2 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: "bold" }}
                          >
                            Score {rubricItem.score}:
                          </Typography>

                          <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            value={rubricItem.description}
                            onChange={(e) =>
                              onRubricChange(
                                question.question.id,
                                rubricItem.score,
                                "description",
                                e.target.value
                              )
                            }
                            disabled={!isEditing}
                            sx={{ ml: 2, mt: 0.5 }}
                            label="Description"
                          />

                          <TextField
                            fullWidth
                            multiline
                            minRows={1}
                            value={rubricItem.example ?? ""}
                            onChange={(e) =>
                              onRubricChange(
                                question.question.id,
                                rubricItem.score,
                                "example",
                                e.target.value
                              )
                            }
                            disabled={!isEditing}
                            sx={{ ml: 2, mt: 1 }}
                            label="Example (optional)"
                            placeholder='e.g. "I assess fish by..."'
                          />
                        </Box>
                      ))}
                  </Paper>
                </Box>
              );
            })()}
          </AccordionDetails>
        </Accordion>

        {/* Add Dimension Dialog */}
        <AddDimensionDialog
          open={addDimensionDialog}
          onClose={() => setAddDimensionDialog(false)}
          onAdd={handleAddDimension}
          newDimension={newDimension}
          onDimensionChange={setNewDimension}
        />

        {/* Remove Rubric Confirmation Dialog */}
        <Dialog
          open={removeRubricDialog}
          onClose={() => setRemoveRubricDialog(false)}
        >
          <DialogTitle>Remove Rubric?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to remove the rubric from this question?
              This will remove all scoring criteria (scores 1-5) and the
              question will no longer be scored during the interview.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRemoveRubricDialog(false)}>Cancel</Button>
            <Button
              onClick={() => {
                onQuestionChange(question.question.id, "rubric", undefined);
                setRemoveRubricDialog(false);
              }}
              color="error"
              variant="contained"
            >
              Remove Rubric
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
);

SortableQuestion.displayName = "SortableQuestion";

// Debounce hook
const useDebounce = <T,>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const InterviewPlanEditor: React.FC<InterviewPlanEditorProps> = ({
  interviewPlan,
  onChange,
  isEditing,
}) => {
  // Create a deep copy of the plan to avoid mutation issues
  const createDeepCopy = useCallback((plan: InterviewPlan): InterviewPlan => {
    return JSON.parse(JSON.stringify(plan));
  }, []);

  const [localPlan, setLocalPlan] = useState<InterviewPlan>(() =>
    createDeepCopy(interviewPlan)
  );
  const [addQuestionDialog, setAddQuestionDialog] = useState(false);
  const [newQuestion, setNewQuestion] = useState<Partial<Question>>({
    question: "",
    question_type: "general",
  });

  // Increase debounce delay for better performance
  const debouncedPlan = useDebounce(localPlan, 500);

  // Only call onChange when the debounced value changes
  useEffect(() => {
    if (isEditing) {
      onChange(debouncedPlan);
    }
  }, [debouncedPlan, onChange, isEditing]);

  // Only update local plan when we're not editing
  useEffect(() => {
    if (!isEditing) {
      setLocalPlan(createDeepCopy(interviewPlan));
    }
  }, [interviewPlan, isEditing, createDeepCopy]);

  // Optimized update functions that modify state immutably without deep copy
  const updateLocalPlan = useCallback(
    (updater: (plan: InterviewPlan) => void) => {
      setLocalPlan((prevPlan) => {
        const newPlan = createDeepCopy(prevPlan);
        updater(newPlan);
        return newPlan;
      });
    },
    [createDeepCopy]
  );

  const handleScenarioChange = useCallback(
    (field: string, value: string) => {
      updateLocalPlan((plan) => {
        (plan.scenario as any)[field] = value;
      });
    },
    [updateLocalPlan]
  );

  const handleCriteriaChange = useCallback(
    (index: number, value: string) => {
      updateLocalPlan((plan) => {
        plan.eval_criteria[index] = value;
      });
    },
    [updateLocalPlan]
  );

  const addCriteria = useCallback(() => {
    updateLocalPlan((plan) => {
      plan.eval_criteria.push("");
    });
  }, [updateLocalPlan]);

  const removeCriteria = useCallback(
    (index: number) => {
      updateLocalPlan((plan) => {
        plan.eval_criteria.splice(index, 1);
      });
    },
    [updateLocalPlan]
  );

  const handleQuestionChange = useCallback(
    (questionId: string, field: string, value: any) => {
      updateLocalPlan((plan) => {
        const questionIndex = plan.questions.findIndex(
          (q) => q.question.id === questionId
        );
        if (questionIndex !== -1) {
          // Handle rubric field separately (it's on InterviewQuestion, not Question)
          if (field === "rubric") {
            plan.questions[questionIndex].rubric = value;
          } else {
            (plan.questions[questionIndex].question as any)[field] = value;
          }
        }
      });
    },
    [updateLocalPlan]
  );

  const handleDimensionChange = useCallback(
    (
      questionId: string,
      dimensionIndex: number,
      field: keyof Dimension,
      value: any
    ) => {
      updateLocalPlan((plan) => {
        const questionIndex = plan.questions.findIndex(
          (q) => q.question.id === questionId
        );
        if (questionIndex !== -1 && plan.questions[questionIndex].dimensions) {
          // Ensure we're updating the correct dimension by index
          if (plan.questions[questionIndex].dimensions![dimensionIndex]) {
            (plan.questions[questionIndex].dimensions![dimensionIndex] as any)[
              field
            ] = value;
          }
        }
      });
    },
    [updateLocalPlan]
  );

  const addDimension = useCallback(
    (questionId: string, dim: Dimension) => {
      updateLocalPlan((plan) => {
        const questionIndex = plan.questions.findIndex(
          (q) => q.question.id === questionId
        );
        if (questionIndex !== -1) {
          if (!plan.questions[questionIndex].dimensions) {
            plan.questions[questionIndex].dimensions = [];
          }

          // stable id
          const id =
            dim.id && dim.id.trim()
              ? dim.id
              : `temp_${Date.now()}_${Math.random()}`;

          // Avoid TS2783: spread first, then only add defaults conditionally
          const withDefaults: Dimension = {
            ...dim,
            id,
          };

          plan.questions[questionIndex].dimensions!.push(withDefaults);
        }
      });
    },
    [updateLocalPlan]
  );
  const removeDimension = useCallback(
    (questionId: string, dimensionIndex: number) => {
      updateLocalPlan((plan) => {
        const questionIndex = plan.questions.findIndex(
          (q) => q.question.id === questionId
        );
        if (questionIndex !== -1 && plan.questions[questionIndex].dimensions) {
          // Remove the dimension at the specific index
          plan.questions[questionIndex].dimensions!.splice(dimensionIndex, 1);
        }
      });
    },
    [updateLocalPlan]
  );

  const handleDimensionReorder = useCallback(
    (questionId: string, oldIndex: number, newIndex: number) => {
      updateLocalPlan((plan) => {
        const questionIndex = plan.questions.findIndex(
          (q) => q.question.id === questionId
        );
        if (questionIndex !== -1 && plan.questions[questionIndex].dimensions) {
          const dimensions = [...plan.questions[questionIndex].dimensions!];
          const [movedDimension] = dimensions.splice(oldIndex, 1);
          dimensions.splice(newIndex, 0, movedDimension);
          plan.questions[questionIndex].dimensions = dimensions;
        }
      });
    },
    [updateLocalPlan]
  );

  const handleRubricChange = useCallback(
    (
      questionId: string,
      score: number,
      field: "description" | "example",
      value: string
    ) => {
      updateLocalPlan((plan) => {
        const questionIndex = plan.questions.findIndex(
          (q) => q.question.id === questionId
        );
        if (questionIndex !== -1) {
          // Only modify rubric if it already exists - never create a new one
          if (plan.questions[questionIndex].rubric) {
            const rubricIndex = plan.questions[questionIndex].rubric!.findIndex(
              (r) => r.score === score
            );
            if (rubricIndex !== -1) {
              plan.questions[questionIndex].rubric![rubricIndex][field] = value;
            } else {
              // If the specific score doesn't exist in the rubric, add it
              // This handles cases where rubric exists but is incomplete
              plan.questions[questionIndex].rubric!.push({
                score,
                description: field === "description" ? value : "",
                example: field === "example" ? value : "",
              });
              // Sort rubric by score descending
              plan.questions[questionIndex].rubric!.sort(
                (a, b) => b.score - a.score
              );
            }
          }
          // If rubric doesn't exist, do nothing - don't create defaults
        }
      });
    },
    [updateLocalPlan]
  );

  const addQuestion = useCallback(
    (question: Partial<Question>) => {
      updateLocalPlan((plan) => {
        const qType = question.question_type || "general";
        const newQ: InterviewQuestion = {
          question: {
            id: `q_${Date.now()}`,
            question: question.question || "",
            question_type: qType,
            would_interviewer_know_answer: false,
            is_question_multi_part_explanation: false,
            would_answer_overlap_another_question: false,
          },
          dimensions: [],
          // Only add a default rubric for non-informational questions
          rubric:
            qType === "informational"
              ? undefined
              : [
                  {
                    score: 5,
                    description: "Exceptional - Exceeds all expectations",
                  },
                  {
                    score: 4,
                    description: "Strong - Meets most expectations well",
                  },
                  {
                    score: 3,
                    description: "Adequate - Meets basic expectations",
                  },
                  { score: 2, description: "Developing - Below expectations" },
                  {
                    score: 1,
                    description: "Inadequate - Does not meet expectations",
                  },
                ],
        };
        plan.questions.push(newQ);
      });
      setAddQuestionDialog(false);
      setNewQuestion({ question: "", question_type: "general" });
    },
    [updateLocalPlan]
  );

  const removeQuestion = useCallback(
    (questionId: string) => {
      updateLocalPlan((plan) => {
        const questionIndex = plan.questions.findIndex(
          (q) => q.question.id === questionId
        );
        if (questionIndex !== -1) {
          plan.questions.splice(questionIndex, 1);
        }
      });
    },
    [updateLocalPlan]
  );

  // Handlers for allowed_qa_topics
  const handleQATopicChange = useCallback(
    (index: number, value: string) => {
      updateLocalPlan((plan) => {
        if (!plan.editor_fields.allowed_qa_topics) {
          plan.editor_fields.allowed_qa_topics = [];
        }
        plan.editor_fields.allowed_qa_topics[index] = value;
      });
    },
    [updateLocalPlan]
  );

  const addQATopic = useCallback(() => {
    updateLocalPlan((plan) => {
      if (!plan.editor_fields.allowed_qa_topics) {
        plan.editor_fields.allowed_qa_topics = [];
      }
      plan.editor_fields.allowed_qa_topics.push("");
    });
  }, [updateLocalPlan]);

  const removeQATopic = useCallback(
    (index: number) => {
      updateLocalPlan((plan) => {
        if (plan.editor_fields.allowed_qa_topics) {
          plan.editor_fields.allowed_qa_topics.splice(index, 1);
        }
      });
    },
    [updateLocalPlan]
  );

  // Handlers for additional_guidance
  const handleAdditionalGuidanceChange = useCallback(
    (index: number, value: string) => {
      updateLocalPlan((plan) => {
        if (!plan.editor_fields.additional_guidance) {
          plan.editor_fields.additional_guidance = [];
        }
        plan.editor_fields.additional_guidance[index] = value;
      });
    },
    [updateLocalPlan]
  );

  const addAdditionalGuidance = useCallback(() => {
    updateLocalPlan((plan) => {
      if (!plan.editor_fields.additional_guidance) {
        plan.editor_fields.additional_guidance = [];
      }
      plan.editor_fields.additional_guidance.push("");
    });
  }, [updateLocalPlan]);

  const removeAdditionalGuidance = useCallback(
    (index: number) => {
      updateLocalPlan((plan) => {
        if (plan.editor_fields.additional_guidance) {
          plan.editor_fields.additional_guidance.splice(index, 1);
        }
      });
    },
    [updateLocalPlan]
  );

  // Memoize handlers for simple field updates
  const handleEditorFieldChange = useCallback(
    (field: string, value: any) => {
      updateLocalPlan((plan) => {
        (plan.editor_fields as any)[field] = value;
      });
    },
    [updateLocalPlan]
  );

  // Handler for post_interview_steps
  const handlePostInterviewStepsChange = useCallback(
    (steps: PostInterviewStep[]) => {
      updateLocalPlan((plan) => {
        plan.editor_fields.post_interview_steps = steps;
      });
    },
    [updateLocalPlan]
  );

  // Handler for controversial_content_config
  const handleControversialContentConfigChange = useCallback(
    (config: ControversialContentConfig | null) => {
      updateLocalPlan((plan) => {
        plan.editor_fields.controversial_content_config = config;
      });
    },
    [updateLocalPlan]
  );

  const handleConfigurationChange = useCallback(
    (field: string, value: any) => {
      updateLocalPlan((plan) => {
        if (plan.configuration) {
          (plan.configuration as any)[field] = value;
        }
      });
    },
    [updateLocalPlan]
  );

  const handleSimpleFieldChange = useCallback(
    (field: string, value: any) => {
      updateLocalPlan((plan) => {
        (plan as any)[field] = value;
      });
    },
    [updateLocalPlan]
  );

  // Drag sensors for questions
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleQuestionDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (over && active.id !== over.id) {
        updateLocalPlan((plan) => {
          const oldIndex = plan.questions.findIndex(
            (q) => q.question.id === active.id
          );
          const newIndex = plan.questions.findIndex(
            (q) => q.question.id === over.id
          );

          if (oldIndex !== -1 && newIndex !== -1) {
            // Use arrayMove to preserve all question properties including rubric
            const reorderedQuestions = arrayMove(
              plan.questions,
              oldIndex,
              newIndex
            );

            // Deep copy to ensure all nested properties are preserved
            plan.questions = reorderedQuestions.map((q) => ({
              question: { ...q.question },
              dimensions: q.dimensions ? [...q.dimensions] : undefined,
              rubric: q.rubric
                ? [...q.rubric.map((r) => ({ ...r }))]
                : undefined,
            }));
          }
        });
      }
    },
    [updateLocalPlan]
  );

  // Memoize question IDs for better performance
  const questionIds = useMemo(
    () => localPlan.questions.map((q) => q.question.id),
    [localPlan.questions]
  );

  return (
    <Box>
      {/* Scenario Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Scenario Configuration
          </Typography>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Scenario Description"
              fullWidth
              multiline
              rows={3}
              value={localPlan.scenario.description}
              onChange={(e) =>
                handleScenarioChange("description", e.target.value)
              }
              disabled={!isEditing}
            />
            <TextField
              label="Scenario Goal"
              fullWidth
              value={localPlan.scenario.goal}
              onChange={(e) => handleScenarioChange("goal", e.target.value)}
              disabled={!isEditing}
            />
            <Box display="flex" gap={2}>
              <FormControl fullWidth disabled={!isEditing}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={localPlan.scenario.category}
                  onChange={(e) =>
                    handleScenarioChange("category", e.target.value)
                  }
                  label="Category"
                >
                  <MenuItem value="interview">Interview</MenuItem>
                  <MenuItem value="general">General</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Applicant"
                fullWidth
                value={localPlan.scenario.applicant}
                onChange={(e) =>
                  handleScenarioChange("applicant", e.target.value)
                }
                disabled={!isEditing}
              />
            </Box>
            <Box display="flex" gap={2}>
              <TextField
                label="Evaluator"
                fullWidth
                value={localPlan.scenario.evaluator}
                onChange={(e) =>
                  handleScenarioChange("evaluator", e.target.value)
                }
                disabled={!isEditing}
              />
              <TextField
                label="Topics to Exclude"
                fullWidth
                value={localPlan.scenario.no_include || ""}
                onChange={(e) =>
                  handleScenarioChange("no_include", e.target.value)
                }
                disabled={!isEditing}
                placeholder="Topics that should not be included"
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Editor Fields & Configuration */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Interview Settings
          </Typography>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Greeting"
              fullWidth
              multiline
              rows={2}
              value={localPlan.editor_fields.greeting}
              onChange={(e) =>
                handleEditorFieldChange("greeting", e.target.value)
              }
              disabled={!isEditing}
            />
            <Box display="flex" gap={2}>
              <TextField
                label="Expected Time (minutes)"
                fullWidth
                value={localPlan.editor_fields.expected_time_minutes}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow empty string while typing
                  if (value === "") {
                    handleEditorFieldChange("expected_time_minutes", 0);
                    return;
                  }
                  // Only allow digits
                  if (/^\d+$/.test(value)) {
                    const numValue = parseInt(value, 10);
                    handleEditorFieldChange("expected_time_minutes", numValue);
                  }
                }}
                disabled={!isEditing}
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
              />
              <TextField
                label="Time Limit (minutes)"
                fullWidth
                value={localPlan.editor_fields.time_limit_minutes}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow empty string while typing
                  if (value === "") {
                    handleEditorFieldChange("time_limit_minutes", 0);
                    return;
                  }
                  // Only allow digits
                  if (/^\d+$/.test(value)) {
                    const numValue = parseInt(value, 10);
                    handleEditorFieldChange("time_limit_minutes", numValue);
                  }
                }}
                disabled={!isEditing}
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
              />
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={
                    localPlan.editor_fields.ask_for_questions_before_start ??
                    true
                  }
                  onChange={(e) =>
                    handleEditorFieldChange(
                      "ask_for_questions_before_start",
                      e.target.checked
                    )
                  }
                  disabled={!isEditing}
                />
              }
              label="Ask for questions at beginning"
            />

            {/* Advanced Interview Settings */}
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              Advanced Interview Settings
            </Typography>

            <FormControlLabel
              control={
                <Checkbox
                  checked={
                    localPlan.editor_fields.check_for_suspicious !== false
                  }
                  onChange={(e) =>
                    handleEditorFieldChange(
                      "check_for_suspicious",
                      e.target.checked
                    )
                  }
                  disabled={!isEditing}
                />
              }
              label="Check for suspicious statements"
              sx={{ mb: 1 }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={
                    localPlan.editor_fields.check_for_seeking_clarification !==
                    false
                  }
                  onChange={(e) =>
                    handleEditorFieldChange(
                      "check_for_seeking_clarification",
                      e.target.checked
                    )
                  }
                  disabled={!isEditing}
                />
              }
              label="Check for seeking clarification"
              sx={{ mb: 1 }}
            />

            <TextField
              label="Max User Queries Per Question"
              fullWidth
              value={
                localPlan.editor_fields.max_num_user_queries_per_question ?? 3
              }
              onChange={(e) => {
                const value = e.target.value;
                // Allow empty string while typing
                if (value === "") {
                  handleEditorFieldChange(
                    "max_num_user_queries_per_question",
                    0
                  );
                  return;
                }
                // Only allow digits
                if (/^\d+$/.test(value)) {
                  const numValue = parseInt(value, 10);
                  handleEditorFieldChange(
                    "max_num_user_queries_per_question",
                    numValue
                  );
                }
              }}
              disabled={!isEditing}
              helperText="Maximum number of clarifying questions per interview question (0 = no limit)"
              sx={{ mb: 2 }}
              inputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*",
              }}
            />

            <TextField
              label="Max Poorly Answered Questions"
              fullWidth
              value={
                localPlan.editor_fields.max_num_poorly_answered_questions ?? 2
              }
              onChange={(e) => {
                const value = e.target.value;
                // Allow empty string while typing
                if (value === "") {
                  handleEditorFieldChange(
                    "max_num_poorly_answered_questions",
                    0
                  );
                  return;
                }
                // Only allow digits
                if (/^\d+$/.test(value)) {
                  const numValue = parseInt(value, 10);
                  handleEditorFieldChange(
                    "max_num_poorly_answered_questions",
                    numValue
                  );
                }
              }}
              disabled={!isEditing}
              helperText="Number of poorly answered questions before wrapping up (0 = no check)"
              sx={{ mb: 2 }}
              inputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*",
              }}
            />

            <Box display="flex" gap={2}>
              <TextField
                label="Min Words for Chat"
                fullWidth
                value={localPlan.editor_fields.min_words_for_chat ?? 0}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow empty string while typing
                  if (value === "") {
                    handleEditorFieldChange("min_words_for_chat", 0);
                    return;
                  }
                  // Only allow digits
                  if (/^\d+$/.test(value)) {
                    const numValue = parseInt(value, 10);
                    handleEditorFieldChange("min_words_for_chat", numValue);
                  }
                }}
                disabled={!isEditing}
                helperText="Minimum words for casual chatting (0 = no chat)"
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
              />
              <TextField
                label="Max Words for Chat"
                fullWidth
                value={localPlan.editor_fields.max_words_for_chat ?? 0}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow empty string while typing
                  if (value === "") {
                    handleEditorFieldChange("max_words_for_chat", 0);
                    return;
                  }
                  // Only allow digits
                  if (/^\d+$/.test(value)) {
                    const numValue = parseInt(value, 10);
                    handleEditorFieldChange("max_words_for_chat", numValue);
                  }
                }}
                disabled={!isEditing}
                helperText="Maximum words for casual chatting (0 = no limit)"
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Timeout Settings */}
            <Typography variant="subtitle2" gutterBottom>
              Timeout Settings
            </Typography>

            <Box display="flex" gap={2} sx={{ mb: 2 }}>
              <TextField
                label="Post Insight Timeout (seconds)"
                fullWidth
                value={
                  localPlan.editor_fields.post_insight_timeout_seconds ?? -2
                }
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow negative values and decimals for timeout settings
                  const numValue = parseFloat(value);
                  if (!isNaN(numValue)) {
                    handleEditorFieldChange(
                      "post_insight_timeout_seconds",
                      numValue
                    );
                  }
                }}
                disabled={!isEditing}
                helperText="-3: debug, -2: default, -1: never, positive: seconds"
                inputProps={{
                  step: 0.1,
                }}
              />
              <TextField
                label="Post Statement Timeout (seconds)"
                fullWidth
                value={
                  localPlan.editor_fields.post_statement_timeout_seconds ?? -2
                }
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow negative values and decimals for timeout settings
                  const numValue = parseFloat(value);
                  if (!isNaN(numValue)) {
                    handleEditorFieldChange(
                      "post_statement_timeout_seconds",
                      numValue
                    );
                  }
                }}
                disabled={!isEditing}
                helperText="-3: debug, -2: default, -1: never, positive: seconds"
                inputProps={{
                  step: 0.1,
                }}
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            <TextField
              label="Without Guidance"
              fullWidth
              multiline
              rows={2}
              value={localPlan.editor_fields.without_guidance || ""}
              onChange={(e) =>
                handleEditorFieldChange("without_guidance", e.target.value)
              }
              disabled={!isEditing}
              helperText="Guidance for the interviewer on how to inject their own thoughts (assume that without is being prefixed to each of these fields) (e.g., 'influencing the candidate's decision or providing personal opinions')"
              sx={{ mb: 2 }}
            />

            <TextField
              label="Discussion Topic"
              fullWidth
              multiline
              rows={2}
              value={localPlan.discussion_topic}
              onChange={(e) =>
                handleSimpleFieldChange("discussion_topic", e.target.value)
              }
              disabled={!isEditing}
            />
            <TextField
              label="Criteria Context"
              fullWidth
              multiline
              rows={3}
              value={localPlan.criteria_context}
              onChange={(e) =>
                handleSimpleFieldChange("criteria_context", e.target.value)
              }
              disabled={!isEditing}
            />
          </Box>

          {/* Q&A Topics Section */}
          <Box mt={3}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="subtitle1">Allowed Q&A Topics</Typography>
              {isEditing && (
                <Button
                  startIcon={<AddIcon />}
                  onClick={addQATopic}
                  size="small"
                  variant="outlined"
                >
                  Add Topic
                </Button>
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Topics that can be discussed during the interview Q&A section
            </Typography>
            {localPlan.editor_fields.allowed_qa_topics &&
            localPlan.editor_fields.allowed_qa_topics.length > 0 ? (
              <List>
                {localPlan.editor_fields.allowed_qa_topics.map(
                  (topic, index) => (
                    <ListItem key={index}>
                      <TextField
                        fullWidth
                        value={topic}
                        onChange={(e) =>
                          handleQATopicChange(index, e.target.value)
                        }
                        disabled={!isEditing}
                        placeholder={`Q&A Topic ${index + 1}`}
                        size="small"
                      />
                      {isEditing && (
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            onClick={() => removeQATopic(index)}
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      )}
                    </ListItem>
                  )
                )}
              </List>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontStyle: "italic" }}
              >
                No Q&A topics defined
              </Typography>
            )}
          </Box>

          {/* Additional Guidance Section */}
          <Box mt={3}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="subtitle1">Additional Guidance</Typography>
              {isEditing && (
                <Button
                  startIcon={<AddIcon />}
                  onClick={addAdditionalGuidance}
                  size="small"
                  variant="outlined"
                >
                  Add Guidance
                </Button>
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Additional guidance for the interviewer added to prompts
            </Typography>
            {localPlan.editor_fields.additional_guidance &&
            localPlan.editor_fields.additional_guidance.length > 0 ? (
              <List>
                {localPlan.editor_fields.additional_guidance.map(
                  (guidance, index) => (
                    <ListItem key={index}>
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        value={guidance}
                        onChange={(e) =>
                          handleAdditionalGuidanceChange(index, e.target.value)
                        }
                        disabled={!isEditing}
                        placeholder={`Guidance ${index + 1}`}
                        size="small"
                      />
                      {isEditing && (
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            onClick={() => removeAdditionalGuidance(index)}
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      )}
                    </ListItem>
                  )
                )}
              </List>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontStyle: "italic" }}
              >
                No additional guidance defined
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Configuration Settings */}
      {localPlan.configuration && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Advanced Configuration
            </Typography>
            <Box display="flex" flexDirection="column" gap={2}>
              <Box display="flex" gap={2}>
                <TextField
                  label="Min Number of Turns"
                  type="number"
                  fullWidth
                  value={localPlan.configuration.min_num_turns}
                  onChange={(e) =>
                    handleConfigurationChange(
                      "min_num_turns",
                      parseInt(e.target.value) || 0
                    )
                  }
                  disabled={!isEditing}
                />
                <TextField
                  label="Max Number of Turns"
                  type="number"
                  fullWidth
                  value={localPlan.configuration.max_num_turns}
                  onChange={(e) =>
                    handleConfigurationChange(
                      "max_num_turns",
                      parseInt(e.target.value) || 0
                    )
                  }
                  disabled={!isEditing}
                />
                <TextField
                  label="Min Questions Answered"
                  type="number"
                  fullWidth
                  value={localPlan.configuration.min_num_questions_answered}
                  onChange={(e) =>
                    handleConfigurationChange(
                      "min_num_questions_answered",
                      parseInt(e.target.value) || 0
                    )
                  }
                  disabled={!isEditing}
                />
              </Box>
              <Box display="flex" gap={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={localPlan.configuration.non_job}
                      onChange={(e) =>
                        handleConfigurationChange("non_job", e.target.checked)
                      }
                      disabled={!isEditing}
                    />
                  }
                  label="Non-Job Interview"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={localPlan.configuration.positive_feedback}
                      onChange={(e) =>
                        handleConfigurationChange(
                          "positive_feedback",
                          e.target.checked
                        )
                      }
                      disabled={!isEditing}
                    />
                  }
                  label="Positive Feedback"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={localPlan.configuration.reflective_responses}
                      onChange={(e) =>
                        handleConfigurationChange(
                          "reflective_responses",
                          e.target.checked
                        )
                      }
                      disabled={!isEditing}
                    />
                  }
                  label="Reflective Responses"
                />
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Evaluation Criteria */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6">Evaluation Criteria</Typography>
            {isEditing && (
              <Button
                startIcon={<AddIcon />}
                onClick={addCriteria}
                size="small"
                variant="outlined"
              >
                Add Criteria
              </Button>
            )}
          </Box>
          <List>
            {localPlan.eval_criteria.map((criteria, index) => (
              <ListItem key={index}>
                <TextField
                  fullWidth
                  value={criteria}
                  onChange={(e) => handleCriteriaChange(index, e.target.value)}
                  disabled={!isEditing}
                  placeholder={`Criteria ${index + 1}`}
                />
                {isEditing && (
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeCriteria(index);
                      }}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                )}
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>

      {/* Post-Interview Steps (Outros) */}
      <Box sx={{ mb: 3 }}>
        <PostInterviewStepsEditor
          steps={localPlan.editor_fields.post_interview_steps || []}
          onChange={handlePostInterviewStepsChange}
          isEditing={isEditing}
        />
      </Box>

      {/* Controversial Content Detection */}
      <ControversialContentEditor
        config={localPlan.editor_fields.controversial_content_config}
        onChange={handleControversialContentConfigChange}
        isEditing={isEditing}
      />

      {/* Questions Section with Drag and Drop */}
      <Card>
        <CardContent>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6">
              Interview Questions ({localPlan.questions.length})
            </Typography>
            {isEditing && (
              <Button
                startIcon={<AddIcon />}
                onClick={() => setAddQuestionDialog(true)}
                size="small"
                variant="outlined"
              >
                Add Question
              </Button>
            )}
          </Box>

          <Box sx={{ position: "relative", pl: isEditing ? 5 : 0 }}>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleQuestionDragEnd}
            >
              <SortableContext
                items={questionIds}
                strategy={verticalListSortingStrategy}
                disabled={!isEditing}
              >
                {localPlan.questions.map((question, index) => (
                  <SortableQuestion
                    key={question.question.id}
                    question={question}
                    index={index}
                    isEditing={isEditing}
                    onQuestionChange={handleQuestionChange}
                    onDimensionChange={handleDimensionChange}
                    onAddDimension={addDimension}
                    onRemoveDimension={removeDimension}
                    onRemoveQuestion={removeQuestion}
                    onRubricChange={handleRubricChange}
                    onDimensionReorder={handleDimensionReorder}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </Box>
        </CardContent>
      </Card>

      {/* Assessment Processing Type */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Assessment Processing Type
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Determines whether assessment is evaluated via memory (Solana usecase) or used to fill a form (doctor use case)
          </Typography>
          <FormControl fullWidth disabled={!isEditing}>
            <InputLabel>Processing Type</InputLabel>
            <Select
              value={localPlan.assessment_processing_type}
              onChange={(e) =>
                handleSimpleFieldChange(
                  "assessment_processing_type",
                  e.target.value
                )
              }
              label="Processing Type"
            >
              <MenuItem value="memory">Memory</MenuItem>
              <MenuItem value="form">Form</MenuItem>
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {/* Add Question Dialog */}
      <AddQuestionDialog
        open={addQuestionDialog}
        onClose={() => setAddQuestionDialog(false)}
        onAdd={addQuestion}
        newQuestion={newQuestion}
        onQuestionChange={setNewQuestion}
      />
    </Box>
  );
};

export default InterviewPlanEditor;
